import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { LogbookInput, LogbookKind } from '@syndiqia/maintenance/contracts';
import { errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAssets, useLogbook, useMaintenanceMutation, useVendors } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { MaintenanceFrame, centsOf, todayIso } from './MaintenanceNav';

export function LogbookPage() {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const [params, setParams] = useSearchParams();
  const assetId = params.get('asset') ?? '';
  const assets = useAssets();
  const entries = useLogbook(assetId || undefined);

  return (
    <MaintenanceFrame
      intro={m.logbookIntro}
      actions={
        <>
          <button type="button" className="button no-print" onClick={() => window.print()}>
            {m.print}
          </button>
          {s.can('maintenance.manage') && <Disclosure label={m.addEntry}>{(close) => <EntryForm onDone={close} />}</Disclosure>}
        </>
      }
    >
      <p className="muted small">{m.logbookProfessional}</p>
      <label className="inline-field no-print">
        {m.asset}
        <select value={assetId} onChange={(e) => setParams(e.target.value ? { asset: e.target.value } : {})}>
          <option value="">{m.allAssets}</option>
          {assets.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </label>
      {entries.isPending && <Loading label={t.loading} />}
      {entries.isError && <Alert>{errorMessage(entries.error, t)}</Alert>}
      {entries.data?.length === 0 && <Empty>{m.noEntries}</Empty>}
      {!!entries.data?.length && (
        <ol className="logbook">
          {entries.data.map((e) => (
            <li key={e.id} className={`logbook-entry logbook-${e.kind}`}>
              <div className="logbook-date">{date(e.entryDate)}</div>
              <div className="logbook-body">
                <div>
                  <span className="badge">{m.logbookKinds[e.kind]}</span> <strong>{e.title}</strong>
                </div>
                {e.description && <p className="message-body small">{e.description}</p>}
                <p className="muted small task-facts">
                  {e.assetName && <span>{e.assetName}</span>}
                  {e.vendorName && <span>{e.vendorName}</span>}
                  {e.costCents != null && <span>{money(e.costCents)}</span>}
                  {e.recordedByName && <span>{m.recordedBy(e.recordedByName)}</span>}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </MaintenanceFrame>
  );
}

function EntryForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const assets = useAssets();
  const vendors = useVendors();
  const [values, setValues] = useState({ entryDate: todayIso(), kind: 'inspection', title: '', description: '', assetId: '', vendorId: '', cost: '' });
  const [errors, setErrors] = useState<FieldErrors>({});
  const add = useMaintenanceMutation<LogbookInput, { id: string }>((api, base, input) => api(`${base}/logbook`, { method: 'POST', body: input }));
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { cost, ...rest } = values;
    const result = validate(LogbookInput, { ...rest, costCents: centsOf(cost) });
    setErrors(result.errors ?? {});
    if (result.data) add.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={m.entryDate} error={errors.entryDate}>
          <input type="date" value={values.entryDate} max={todayIso()} onChange={set('entryDate')} />
        </Field>
        <Field label={m.kind}>
          <select value={values.kind} onChange={set('kind')}>
            {LogbookKind.options.map((k) => (
              <option key={k} value={k}>
                {m.logbookKinds[k]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={m.entryTitle} error={errors.title}>
        <input value={values.title} onChange={set('title')} autoFocus />
      </Field>
      <Field label={m.description} hint={t.common.optional}>
        <textarea rows={3} value={values.description} onChange={set('description')} />
      </Field>
      <div className="row">
        <Field label={m.asset} hint={t.common.optional}>
          <select value={values.assetId} onChange={set('assetId')}>
            <option value="">{m.none}</option>
            {assets.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={m.vendor} hint={t.common.optional}>
          <select value={values.vendorId} onChange={set('vendorId')}>
            <option value="">{m.none}</option>
            {vendors.data?.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={m.cost} hint={t.common.optional} error={errors.costCents}>
          <input inputMode="decimal" value={values.cost} onChange={set('cost')} placeholder="0,00 $" />
        </Field>
      </div>
      {add.isError && <Alert>{errorMessage(add.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={add.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
